import { useState, useEffect } from 'react'
import { Film, Play, Calendar, MapPin, User } from 'lucide-react'
import { getImageUrl } from '../utils/apiUrl'
import { apiService } from '../services/api'

const Filmographie = () => {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true)
        const response = await apiService.get('/filmographie/published')
        setProjects(response.data)
      } catch (error) {
        console.error('Erreur chargement filmographie:', error)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProjects()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de la filmographie...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Film className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Erreur de chargement
          </h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-12">
        {/* En-tête */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Film className="h-12 w-12 text-logo mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Filmographie</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Découvrez les œuvres cinématographiques que nous avons accompagnées. 
            Des productions locales aux grandes œuvres internationales.
          </p>
        </div>

            {/* Grille des projets */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="group cursor-pointer">
                  {/* Image du projet - Format Allociné */}
                  <div className="relative mb-3 bg-gray-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="aspect-[2/3] relative">
                      {project.photo_jacquette ? (
                        <img 
                          src={getImageUrl(project.photo_jacquette)}
                          alt={project.nom_projet}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <div className={`absolute inset-0 bg-gray-100 flex items-center justify-center ${project.photo_jacquette ? 'hidden' : 'flex'}`}>
                        <Film className="h-12 w-12 text-gray-400" />
                      </div>
                      
                      {/* Badge type de projet */}
                      <div className="absolute top-2 left-2">
                        <span className="bg-logo text-white px-2 py-1 rounded-full text-xs font-medium shadow-md">
                          {project.type_projet}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contenu - Style Allociné compact */}
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-sm group-hover:text-logo transition-colors line-clamp-2">
                      {project.nom_projet.charAt(0).toUpperCase() + project.nom_projet.slice(1).toLowerCase()}
                      {project.annee_sortie && (
                        <span className="text-xs text-gray-500 font-normal ml-2">
                          ({project.annee_sortie})
                        </span>
                      )}
                    </h3>
                    
                    {project.realisateur && (
                      <p className="text-xs text-gray-600 line-clamp-1">
                        De {project.realisateur}
                      </p>
                    )}
                    
                    {project.lieu_tournage && (
                      <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {project.lieu_tournage}
                      </p>
                    )}
                  </div>
                </div>
          ))}
        </div>

        {/* Message si aucun projet */}
        {projects.length === 0 && (
          <div className="text-center py-12">
            <Film className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucun projet disponible
            </h3>
            <p className="text-gray-600">
              Notre filmographie sera bientôt mise à jour avec nos derniers projets.
            </p>
          </div>
        )}

        {/* Section CTA */}
        <div className="mt-16 bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">
            Vous avez un projet cinématographique ?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Contactez-nous pour discuter de vos besoins en matériel professionnel. 
            Nous accompagnons vos projets de la préparation au tournage.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-6 py-3 bg-logo text-white rounded-lg font-medium hover:bg-logo transition-colors"
          >
            Nous contacter
          </a>
        </div>
      </div>
    </div>
  )
}

export default Filmographie
